import axios from "axios";
import { Fragment, useCallback, useEffect, useState } from "react";
import { ThreeDots } from "react-loader-spinner";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import Footer from "../../layout/Footer";
import Loader from "../../layout/Loader";
import Menu from "../../layout/Menu";
import DriftSummaryCards, { usd } from "../../components/DriftSummaryCards";

/**
 * Pay-As-You-Go Drift.
 *
 * The seat Billing Drift page asks "does Stripe charge what this company's users say it
 * should?". A PAYG company has no subscription, so this asks a different question:
 * **did every topo it created turn into money?**
 *
 *     topo created  ->  ledger row  ->  invoice run  ->  Stripe invoice  ->  paid
 *
 * Every link can break silently. The list costs no Stripe calls - it is all computed from
 * Mongo - so it pages freely. Every number rendered is a field on the response; the endpoint
 * does the arithmetic so the two can never disagree.
 */

const REASONS = [
  { value: "ALL", label: "Any drift" },
  { value: "UNMETERED_TOPOS", label: "Unmetered topos" },
  { value: "UNINVOICED_USAGE", label: "Uninvoiced usage" },
  { value: "STUCK_INVOICE_RUN", label: "Stuck invoice run" },
  { value: "EXHAUSTED_INVOICE_RUN", label: "Retries exhausted" },
  { value: "UNCOLLECTED_INVOICE", label: "Invoiced, not collected" },
  { value: "PAYMENT_FAILED", label: "Payment failed" },
  { value: "LEDGER_RUN_MISMATCH", label: "Ledger/invoice mismatch" },
  { value: "NO_STRIPE_CUSTOMER", label: "No Stripe customer" },
];

const REASON_HELP = {
  UNMETERED_TOPOS:
    "Topos were created but no ledger row was written, so they will never be billed. Metering dropped a write. Remediable.",
  UNINVOICED_USAGE:
    "Usage is still pending in a closed period - the invoice run never billed it. Revenue sitting uncollected. Remediable.",
  STUCK_INVOICE_RUN:
    "An invoice run for a closed period never completed. The daily sweep will retry it; if this persists, look at lastError.",
  EXHAUSTED_INVOICE_RUN:
    "The run has used up its retries, so the sweep has given up. This one needs a human.",
  UNCOLLECTED_INVOICE:
    "An invoice was raised but never confirmed paid or failed. The Stripe webhook probably never arrived - check the webhook endpoint.",
  PAYMENT_FAILED: "The customer's card was declined and the invoice is still outstanding.",
  LEDGER_RUN_MISMATCH:
    "The invoice billed a different number of topos than the ledger holds for that period.",
  NO_STRIPE_CUSTOMER:
    "This PAYG company has no Stripe customer, so it cannot be invoiced at all.",
  NO_PAYG_START:
    "paygStartedAt is not set, so metering never knows where to begin.",
  MANUAL_PAY_ON_PAYG:
    "Auto-pay is off on a PAYG company. PAYG has no manual-billing option, so its invoices cannot be collected.",
};

const PaygDrift = () => {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [pagination, setPagination] = useState({ currentPage: 1, totalPages: 0, totalCount: 0 });
  const [loader, setLoader] = useState(true);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [reason, setReason] = useState("ALL");
  const [lookback, setLookback] = useState(3);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const [expanded, setExpanded] = useState(null);
  const [rechecking, setRechecking] = useState(null);
  const [recheckResult, setRecheckResult] = useState({});
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    document.getElementById("page-loader").style.display = "none";
    document.getElementById("page-container").classList.add("show");
  }, []);

  const fetchReport = useCallback(async () => {
    setLoader(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_base_url}api/masterAdmin/payg-drift`,
        {
          auth_code: process.env.REACT_APP_AUTH_CODE,
          page: currentPage,
          limit: pageSize,
          reason,
          lookback,
          search: debouncedSearch,
        },
        { headers: { "Content-Type": "application/json" } }
      );

      if (response.data.success === true) {
        const { rows: reportRows, summary: reportSummary, pagination: reportPagination } =
          response.data.data;
        setRows(reportRows || []);
        setSummary(reportSummary || null);
        setPagination(reportPagination || { currentPage: 1, totalPages: 0, totalCount: 0 });
      } else {
        toast.error(response.data.message || "Could not load the drift report");
      }
    } catch (error) {
      console.error("Error loading PAYG drift:", error);
      toast.error(error.response?.data?.message || "Could not load the drift report");
    } finally {
      setLoader(false);
    }
  }, [currentPage, pageSize, reason, lookback, debouncedSearch]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  /**
   * Deep-check one company. With `apply` it performs the additions-only backfill for that
   * period; without it the response is a dry run showing exactly what would be written.
   */
  const recheck = async (companyId, billingPeriodKey, apply = false) => {
    if (apply && !window.confirm(
      `Write the missing ledger rows for ${billingPeriodKey}?\n\n` +
      "This only ADDS rows for topos that were never metered - it can never remove or " +
      "re-price anything. They will be billed on the next invoice run for that period."
    )) return;

    setRechecking(`${companyId}:${billingPeriodKey || 'all'}`);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_base_url}api/masterAdmin/payg-drift/recheck`,
        {
          auth_code: process.env.REACT_APP_AUTH_CODE,
          companyId,
          billingPeriodKey,
          apply,
        },
        { headers: { "Content-Type": "application/json" } }
      );

      if (response.data.success === true) {
        setRecheckResult((prev) => ({ ...prev, [companyId]: response.data.data }));
        if (apply) {
          const inserted = response.data.data?.backfill?.inserted ?? 0;
          toast.success(`${inserted} ledger row(s) written.`);
          fetchReport();
        }
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Recheck failed.");
    } finally {
      setRechecking(null);
    }
  };

  const exportCsv = async () => {
    // Export the WHOLE report (every page), not just the visible one.
    setExporting(true);
    try {
      let exportRows = rows;

      if (pagination.totalCount > rows.length) {
        try {
          const myurl = `${process.env.REACT_APP_base_url}api/masterAdmin/payg-drift`;
          const fetchPage = (page) =>
            axios.post(
              myurl,
              {
                auth_code: process.env.REACT_APP_AUTH_CODE,
                page,
                limit: 100,
                reason,
                lookback,
                search: debouncedSearch,
              },
              { headers: { "Content-Type": "application/json" } }
            );

          const first = await fetchPage(1);
          if (first.data.success === true) {
            const { rows: allRows, pagination: p } = first.data.data;
            exportRows = allRows || [];

            // The endpoint caps `limit` at 100, so page through the rest.
            for (let page = 2; page <= (p?.totalPages || 1); page += 1) {
              /* eslint-disable no-await-in-loop */
              const next = await fetchPage(page);
              /* eslint-enable no-await-in-loop */
              if (next.data.success === true) exportRows = exportRows.concat(next.data.data.rows || []);
            }
          }
        } catch (error) {
          console.error("Error building full PAYG drift CSV export:", error);
          toast.error("Could not load every page for export - exporting the current page only");
          exportRows = rows;
        }
      }

      const headers = [
        "Company", "Email", "Company ID", "Reasons", "Unmetered topos", "Estimated unbilled $",
        "Uninvoiced usage $", "Uncollected $", "Total at risk $", "Periods affected",
      ];
      const escape = (value) => {
        const str = value === null || value === undefined ? "" : String(value);
        return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
      };
      const lines = [headers.join(",")].concat(
        exportRows.map((row) =>
          [
            row.companyName, row.companyEmail, row.companyId, (row.reasons || []).join("|"),
            row.unmeteredTopos, row.estimatedUnbilledUsd, row.uninvoicedUsageUsd, row.uncollectedUsd,
            row.totalAtRiskUsd,
            (row.periods || []).filter((period) => (period.reasons || []).length).map((period) => period.billingPeriodKey).join("|"),
          ].map(escape).join(",")
        )
      );

      const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `payg-drift-${new Date().toISOString().slice(0, 10)}.csv`;

      // The anchor must be in the document, and the object URL must outlive the click.
      document.body.appendChild(link);
      link.click();

      setTimeout(() => {
        URL.revokeObjectURL(url);
        link.remove();
      }, 0);

      toast.success(`Exported ${exportRows.length} row(s)`);
    } finally {
      setExporting(false);
    }
  };

  const reasonBadge = (code) => (
    <span
      key={code}
      className="badge bg-warning ml-1"
      title={REASON_HELP[code] || code}
      style={{ cursor: "help" }}
    >
      {code.replace(/_/g, " ")}
    </span>
  );

  return (
    <Fragment>
      <Loader />
      <div id="page-container" className="fade page-sidebar-fixed page-header-fixed">
        <Menu />
        <div id="content" className="content">
          <ol className="breadcrumb float-xl-right">
            <li className="breadcrumb-item">
              <Link to="/dashboard">Dashboard</Link>
            </li>
            <li className="breadcrumb-item">
              <Link to="/company">Company</Link>
            </li>
            <li className="breadcrumb-item active">Pay As You Go Drift</li>
          </ol>

          <h1 className="page-header">Pay As You Go Drift</h1>

          <p className="text-muted" style={{ marginTop: -10, marginBottom: 18 }}>
            Traces every topo from creation to payment: created &rarr; metered &rarr; invoiced &rarr;
            collected. Anything that fell out of that chain is listed below. Seat companies are covered
            separately on <Link to="/billing-drift">Billing Drift</Link>.
          </p>

          {summary && (
            <DriftSummaryCards
              items={[
                { label: "Total at risk", value: usd(summary.totalAtRiskUsd), tone: "alert-danger" },
                { label: "Never metered", value: usd(summary.totalEstimatedUnbilledUsd), tone: "alert-warning" },
                { label: "Uninvoiced usage", value: usd(summary.totalUninvoicedUsageUsd), tone: "alert-success" },
                { label: "Invoiced, uncollected", value: usd(summary.totalUncollectedUsd), tone: "alert-info" },
              ]}
            />
          )}

          {summary && (
            <p className="text-muted">
              {summary.companiesWithDrift} of {summary.companiesChecked} Pay-As-You-Go
              company(ies) have drift across {summary.periodsChecked?.join(", ")}.
              {summary.totalUnmeteredTopos > 0 && (
                <> {summary.totalUnmeteredTopos} topo(s) were created but never metered.</>
              )}
            </p>
          )}

          <div className="card">
            <div className="card-body">
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 16 }}>
                <input
                  type="text"
                  className="form-control"
                  style={{ width: 240 }}
                  placeholder="Search company or email"
                  title="Search by company name or email"
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
                />

                <select
                  className="form-select"
                  style={{ width: 210 }}
                  title="Reason"
                  value={reason}
                  onChange={(e) => { setReason(e.target.value); setCurrentPage(1); }}
                >
                  {REASONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>

                <select
                  className="form-select"
                  style={{ width: 170 }}
                  title="How many billing periods back to check"
                  value={lookback}
                  onChange={(e) => { setLookback(Number(e.target.value)); setCurrentPage(1); }}
                >
                  {[1, 3, 6, 12].map((n) => (
                    <option key={n} value={n}>{n === 1 ? "Last month" : `Last ${n} months`}</option>
                  ))}
                </select>

                <select
                  className="form-select"
                  style={{ width: 120 }}
                  title="Rows per page"
                  value={pageSize}
                  onChange={(e) => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}
                >
                  {[20, 50, 100].map((n) => <option key={n} value={n}>{n} / page</option>)}
                </select>

                <button type="button" className="btn btn-outline-secondary" onClick={fetchReport}>
                  Refresh
                </button>
                <button
                  type="button"
                  className="btn btn-outline-primary"
                  onClick={exportCsv}
                  disabled={!rows.length || exporting}
                >
                  {exporting ? "Exporting..." : "Export CSV"}
                </button>
              </div>

              <div className="table-responsive">
                <table className="table table-striped table-bordered">
                  <thead>
                    <tr>
                      <th>Company</th>
                      <th>Never metered</th>
                      <th>Uninvoiced</th>
                      <th>Uncollected</th>
                      <th>At risk</th>
                      <th>Reasons</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loader ? (
                      <tr><td colSpan={7} className="text-center">
                        <ThreeDots color="#348fe2" height={40} width={40} />
                      </td></tr>
                    ) : rows.length === 0 ? (
                      <tr><td colSpan={7} className="text-center text-success">
                        <i className="fa fa-check-circle mr-2"></i>
                        No drift. Every topo created has been metered, invoiced and collected.
                      </td></tr>
                    ) : (
                      rows.map((row) => (
                        <Fragment key={row.companyId}>
                          <tr>
                            <td>
                              <strong>{row.companyName}</strong><br />
                              <small className="text-muted">{row.companyEmail}</small>
                            </td>
                            <td>
                              {row.unmeteredTopos > 0 ? (
                                <>
                                  {row.unmeteredTopos} topo(s)<br />
                                  <small className="text-danger">{usd(row.estimatedUnbilledUsd)}</small>
                                </>
                              ) : <span className="text-muted">&mdash;</span>}
                            </td>
                            <td>{row.uninvoicedUsageUsd > 0 ? usd(row.uninvoicedUsageUsd) : <span className="text-muted">&mdash;</span>}</td>
                            <td>{row.uncollectedUsd > 0 ? usd(row.uncollectedUsd) : <span className="text-muted">&mdash;</span>}</td>
                            <td><strong>{usd(row.totalAtRiskUsd)}</strong></td>
                            <td>{row.reasons.map(reasonBadge)}</td>
                            <td>
                              <button
                                className="btn btn-sm btn-outline-primary"
                                onClick={() => setExpanded(expanded === row.companyId ? null : row.companyId)}
                              >
                                {expanded === row.companyId ? "Hide" : "Periods"}
                              </button>
                            </td>
                          </tr>

                          {expanded === row.companyId && (
                            <tr>
                              <td colSpan={7} className="bg-light">
                                <table className="table table-sm mb-0">
                                  <thead>
                                    <tr>
                                      <th>Period</th>
                                      <th>Topos</th>
                                      <th>Ledger rows</th>
                                      <th>Never metered</th>
                                      <th>Pending</th>
                                      <th>Run</th>
                                      <th>Reasons</th>
                                      <th>Fix</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {row.periods.map((period) => (
                                      <tr key={period.billingPeriodKey}>
                                        <td>{period.label}</td>
                                        <td>{period.toposCreated}</td>
                                        <td>{period.ledgerRows}</td>
                                        <td>
                                          {period.unmeteredTopos > 0
                                            ? <span className="text-danger">{period.unmeteredTopos} ({usd(period.estimatedMissingUsd)})</span>
                                            : <span className="text-muted">&mdash;</span>}
                                        </td>
                                        <td>
                                          {period.pendingRows > 0
                                            ? `${period.pendingRows} (${usd(period.pendingAmountUsd)})`
                                            : <span className="text-muted">&mdash;</span>}
                                        </td>
                                        <td>
                                          {period.runStatus
                                            ? <>
                                                <span className="badge bg-secondary">{period.runStatus}</span>
                                                {period.runAttempts > 1 && <small className="text-muted"> x{period.runAttempts}</small>}
                                                {period.lastError && (
                                                  <div><small className="text-danger" title={period.lastError}>
                                                    {period.lastError.slice(0, 60)}
                                                  </small></div>
                                                )}
                                              </>
                                            : <span className="text-muted">none</span>}
                                        </td>
                                        <td>{period.reasons.map(reasonBadge)}</td>
                                        <td>
                                          {period.unmeteredTopos > 0 && (
                                            <button
                                              className="btn btn-sm btn-outline-danger"
                                              disabled={rechecking === `${row.companyId}:${period.billingPeriodKey}`}
                                              onClick={() => recheck(row.companyId, period.billingPeriodKey, true)}
                                              title="Write the missing ledger rows. Adds only - never removes or re-prices."
                                            >
                                              {rechecking === `${row.companyId}:${period.billingPeriodKey}`
                                                ? <i className="fa fa-spinner fa-spin"></i>
                                                : "Backfill"}
                                            </button>
                                          )}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>

                                {recheckResult[row.companyId]?.backfill?.dryRun && (
                                  <small className="text-muted d-block mt-2">
                                    Dry run: {recheckResult[row.companyId].backfill.wouldInsert} row(s)
                                    would be written at {usd(recheckResult[row.companyId].backfill.unitPrice)} per topo.
                                  </small>
                                )}
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {pagination.totalPages > 1 && (
                <div className="d-flex justify-content-between align-items-center">
                  <small className="text-muted">
                    Page {pagination.currentPage} of {pagination.totalPages} ({pagination.totalCount} companies)
                  </small>
                  <div>
                    <button
                      className="btn btn-sm btn-outline-secondary mr-2"
                      disabled={currentPage <= 1}
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    >
                      Previous
                    </button>
                    <button
                      className="btn btn-sm btn-outline-secondary"
                      disabled={currentPage >= pagination.totalPages}
                      onClick={() => setCurrentPage((p) => p + 1)}
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        <Footer />
      </div>
    </Fragment>
  );
};

export default PaygDrift;
